import React from 'react';
import { Layers, Save } from 'lucide-react';
import { Button, Spinner } from 'react-bootstrap';
import { ImageUploadBox } from './shared/ImageUploadBox';
import { DraggableImageBox } from './shared/DraggableImageBox';

interface GalleryImageEditorProps {
  readonly heroImages: string[];
  readonly selectedGalleryImages: string[];
  readonly fileInputRef: Record<string, HTMLInputElement | null>;
  readonly saveLoading: boolean;
  readonly onGalleryToggle: (imageUrl: string) => void;
  readonly onFileUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  readonly onSaveGalleryImages: () => void;
  readonly onDragStart?: (e: React.DragEvent, imageUrl: string) => void;
}

export const GalleryImageEditor = ({
  heroImages,
  selectedGalleryImages,
  fileInputRef,
  saveLoading,
  onGalleryToggle,
  onFileUpload,
  onSaveGalleryImages,
  onDragStart,
}: GalleryImageEditorProps) => {
  const [imageDimensions, setImageDimensions] = React.useState<Record<string, { width: number; height: number }>>({});

  React.useEffect(() => {
    const loadDimensions = async () => {
      const dimensions: Record<string, { width: number; height: number }> = {};

      const loadImage = (url: string): Promise<{ width: number; height: number }> => {
        return new Promise((resolve) => {
          const img = new Image();
          img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
          img.onerror = () => resolve({ width: 0, height: 0 });
          img.src = url;
        });
      };

      for (const url of heroImages) {
        dimensions[url] = await loadImage(url);
      }

      setImageDimensions(dimensions);
    };

    if (heroImages.length > 0) {
      loadDimensions();
    }
  }, [heroImages]);

  const getImageDimension = (url: string) => {
    if (!imageDimensions[url]) {
      return '';
    }
    const { width, height } = imageDimensions[url];
    return width > 0 && height > 0 ? `${width}×${height}` : '';
  };

  if (heroImages.length === 0) {
    return null;
  }

  return (
    <div
      style={{
        background: 'white',
        borderRadius: '12px',
        padding: '20px',
        border: '2px solid #6c757d',
        marginTop: '24px',
      }}
    >
      <div className="mb-3">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h5 className="mb-0">
            <Layers size={20} className="me-2" />
            Shared Gallery Images
          </h5>
          <Button variant="success" size="sm" onClick={onSaveGalleryImages} disabled={saveLoading}>
            {saveLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" style={{ width: '12px', height: '12px' }} />
                Saving...
              </>
            ) : (
              <>
                <Save size={14} className="me-2" />
                Save Gallery Images
              </>
            )}
          </Button>
        </div>
        <small className="text-muted">
          Select images to add to all products, and non-selected images will not be published to WP.
          <br />
          <span style={{ color: 'red' }}>
            Select Featured Thumbnails first for each product, otherwise, the gallery images will place over the
            featured thumbnails.
          </span>
        </small>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
          gap: '14px',
        }}
      >
        {heroImages.map((url, idx) => (
          <DraggableImageBox
            key={`hero-${idx}`}
            url={url}
            idx={idx}
            isSelected={selectedGalleryImages.includes(url)}
            dimension={getImageDimension(url)}
            showCheckbox
            align="center"
            {...(onDragStart && { onDragStart: (e: React.DragEvent) => onDragStart(e, url) })}
            onToggleCheckbox={() => onGalleryToggle(url)}
          />
        ))}

        <div style={{ textAlign: 'center' }}>
          <div style={{ width: '100%', paddingBottom: '100%', position: 'relative' }}>
            <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}>
              <ImageUploadBox
                inputKey="gallery-shared"
                fileInputRef={fileInputRef}
                onFileUpload={onFileUpload}
                width="100%"
                height="100%"
                fontSize="0.7rem"
                title="Upload custom gallery image"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
